import React, { useEffect, useState } from 'react';
import '@/App.css';
import '@/index.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import Login from '@/pages/Login';
import Setup from '@/pages/Setup';
import Dashboard from '@/pages/Dashboard';
import RoomManagement from '@/pages/RoomManagement';
import RoomDetail from '@/pages/RoomDetail';
import RelayPanel from '@/pages/RelayPanel';
import Settings from '@/pages/Settings';
import Schedules from '@/pages/Schedules';
import Logs from '@/pages/Logs';
import Users from '@/pages/Users';
import Analytics from '@/pages/Analytics';
import { getSetupStatus } from '@/lib/api';
import useSiteSettings from '@/hooks/useSiteSettings';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
};

const AppRoutes = () => {
  const { user } = useAuth();
  const [needsSetup, setNeedsSetup] = useState(null); // null = loading

  useEffect(() => {
    if (!user) {
      getSetupStatus()
        .then(r => setNeedsSetup(r.data.needs_setup))
        .catch(() => setNeedsSetup(false));
    }
  }, [user]);

  // Still checking setup status
  if (!user && needsSetup === null) return null;

  return (
    <Routes>
      <Route path="/setup" element={needsSetup ? <Setup /> : <Navigate to="/login" replace />} />
      <Route path="/login" element={
        user ? <Navigate to="/" replace /> :
        needsSetup ? <Navigate to="/setup" replace /> :
        <Login />
      } />
      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/rooms" element={<ProtectedRoute><RoomManagement /></ProtectedRoute>} />
      <Route path="/rooms/:id" element={<ProtectedRoute><RoomDetail /></ProtectedRoute>} />
      <Route path="/relays" element={<ProtectedRoute><RelayPanel /></ProtectedRoute>} />
      <Route path="/schedules" element={<ProtectedRoute><Schedules /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/logs" element={<ProtectedRoute adminOnly><Logs /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute adminOnly><Users /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute adminOnly><Settings /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to={user ? "/" : needsSetup ? "/setup" : "/login"} replace />} />
    </Routes>
  );
};

function App() {
  useSiteSettings(); // Meta tag ve title güncellemesi
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
